import { createFileRoute, Link } from '@tanstack/react-router'
import { Gift, Package } from 'lucide-react'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { HomeHeader } from '@/widgets/home-header'
import { HomeCTA } from '@/widgets/home-cta'
import { HomeSearch } from '@/widgets/home-search'
import { HomeCategories } from '@/widgets/home-categories'
import { HomeRecent } from '@/widgets/home-recent'
import { HomeRecommendations } from '@/widgets/home-recommendations'
import { HomeCatalog } from '@/widgets/home-catalog'
import { HomeFooter } from '@/widgets/home-footer'
import { HomeScreenBanner } from '@/widgets/homescreen-banner'
import { useProductSets } from '@/entities/product/api/use-products'
import { getSetEffectivePrice } from '@/entities/product/lib/set-logic'

export const Route = createFileRoute('/')({
  component: HomePage,
})

/**
 * Главная страница приложения
 * 
 * Точка входа в приложение с быстрым доступом к основным функциям:
 * - Быстрый старт записи чая в дневник
 * - Поиск по чаю, стилям и заметкам
 * - Популярные направления (категории)
 * - Недавние впечатления
 * - Персональные рекомендации
 * - Каталог чая (интегрированный)
 * 
 * Адаптивный layout:
 * - Mobile: вертикальный поток виджетов
 * - Tablet: 2-колоночная сетка
 * - Desktop: 3-колоночный dashboard с SideNav
 */
function HomePage() {
  // Проверяем наличие наборов — промо-блок показываем только если они есть
  const productSetsQuery = useProductSets({ sort: 'popular', purchasableOnly: true })
  const sets = productSetsQuery.data?.data ?? []
  const hasSets = sets.length > 0
  const fromPrice = hasSets ? Math.min(...sets.map((s) => getSetEffectivePrice(s))) : 0

  return (
    <AppShell showBottomNav>
      <div className="flex-1 overflow-y-auto bg-paper dark:bg-[#0F1113] pt-safe">
        {/* Контейнер с адаптивными отступами */}
        <div className="min-h-full content-container py-6">
          {/* Персональный хедер - full width */}
          <HomeHeader />

          <div className="space-y-6 mt-6">
            <HomeScreenBanner />
            <HomeCTA />
            <HomeSearch />
            <HomeCategories />
            <HomeRecommendations />
            <HomeRecent />

            {/* Промо подарочных наборов — только если они есть в продаже */}
            {hasSets && (
              <Link to="/catalog/$section" params={{ section: 'product_set' }} className="block">
                <div className="flex items-center gap-4 p-4 rounded-2xl bg-gold/5 dark:bg-gold/10 border border-gold/15 dark:border-gold/20 active:scale-[0.98] transition-transform">
                  <div className="w-12 h-12 rounded-2xl bg-gold/15 flex items-center justify-center shrink-0">
                    <Package className="w-6 h-6 text-gold-dark" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink dark:text-dark-text">Подарочные наборы</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Готовые комплекты чая и посуды</p>
                  </div>
                  <span className="text-xs font-medium text-gold-dark shrink-0">от {fromPrice.toLocaleString('ru-RU')} ₽</span>
                </div>
              </Link>
            )}

            <Link to="/gift-cards" className="block">
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-primary/5 dark:bg-primary/10 border border-primary/10 dark:border-primary/20 active:scale-[0.98] transition-transform">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Gift className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-ink dark:text-dark-text">Подарите чай</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Сертификат мгновенно на email</p>
                </div>
                <span className="text-xs font-medium text-primary shrink-0">от 500 ₽</span>
              </div>
            </Link>
          </div>
          
          {/* Каталог чая - full width с адаптивной сеткой */}
          <div className="mt-8">
            <HomeCatalog />
          </div>
          
          {/* Футер контента */}
          <HomeFooter />
        </div>
      </div>
      
      {/* Нижняя навигация (скрыта на desktop) */}
      <BottomNav />
    </AppShell>
  )
}
